import React, { useState } from 'react';
import { Box, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Drawer, useTheme, useMediaQuery, Tooltip, Typography, Avatar } from '@mui/material';
import { Icon } from '@iconify/react';
import { useNavigate, useLocation } from 'react-router-dom';
interface SidebarProps {
  open: boolean;
  onClose: () => void;
  activePage?: string;
}
export const Sidebar: React.FC<SidebarProps> = ({
  open,
  onClose,
  activePage = 'dashboard'
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const drawerWidth = 240;
  const menuItems = [{
    text: 'Dashboard',
    icon: 'lucide:layout-dashboard',
    path: '/',
    id: 'dashboard'
  }, {
    text: 'Transactions',
    icon: 'lucide:banknote',
    path: '/transactions',
    id: 'transactions'
  }, {
    text: 'Failed Transactions',
    icon: 'lucide:alert-circle',
    path: '/failed-transactions',
    id: 'failed-transactions'
  }, {
    text: 'Checker Inbox - Amend',
    icon: 'lucide:inbox',
    path: '/checker-amendments',
    id: 'checker-amendments'
  }, {
    text: 'Checker Inbox - Cancel',
    icon: 'lucide:clipboard-check',
    path: '/checker-cancellations',
    id: 'checker-cancellations'
  }, {
    text: 'Checker Inbox - Settle',
    icon: 'lucide:check-square',
    path: '/checker-settlements',
    id: 'checker-settlements'
  }, {
    text: 'Maker Inbox',
    icon: 'lucide:send',
    path: '/maker-inbox',
    id: 'maker-inbox'
  }, {
    text: 'Reports',
    icon: 'lucide:bar-chart-3',
    path: '/reports',
    id: 'reports'
  }, {
    text: 'Partner Balance',
    icon: 'lucide:wallet',
    path: '/partner-balance',
    id: 'partner-balance'
  }, {
    text: 'Partner Wallet',
    icon: 'lucide:wallet-cards',
    path: '/partner-wallet',
    id: 'partner-wallet'
  }];
  const handleNavigation = (path: string) => {
    // Close the mobile drawer if open
    if (isMobile) {
      onClose();
    }
    navigate(path);
  };
  const drawerContent = <Box sx={{
    width: drawerWidth,
    pt: 1,
    height: '100%',
    backgroundColor: '#F8F9FA',
    borderRight: '1px solid #E5E7EB',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative'
  }} data-id="element-735">
      <Box sx={{
      display: 'flex',
      justifyContent: 'center',
      py: 2
    }} data-id="element-736">
        <Box component="img" src="https://uploadthingy.s3.us-west-1.amazonaws.com/2TFKkyaUakWdHzgEBHAz75/openRemitLogo-1.png" alt="OpenRemit Logo" sx={{
        height: 40
      }} data-id="element-737" />
      </Box>
      <Box sx={{
      px: 2,
      mb: 2
    }} data-id="element-738">
        <Box sx={{
        display: 'flex',
        alignItems: 'center',
        p: 1.5,
        borderRadius: 2,
        backgroundColor: '#F0F3F7',
        border: '1px solid #E5E7EB',
        cursor: 'pointer',
        transition: 'none',
        '&:hover': {
          backgroundColor: '#E5E7EB'
        }
      }} onClick={() => handleNavigation('/change-password')} data-id="element-739">
          <Avatar sx={{
          width: 38,
          height: 38,
          bgcolor: '#0052CC',
          color: 'white',
          fontWeight: 600
        }} data-id="element-740">
            A
          </Avatar>
          <Box sx={{
          ml: 1.5,
          flex: 1
        }} data-id="element-741">
            <Typography variant="subtitle2" sx={{
            fontWeight: 600,
            color: 'text.primary',
            lineHeight: 1.2
          }} data-id="element-742">
              Admin User
            </Typography>
            <Typography variant="caption" sx={{
            color: 'text.secondary',
            fontSize: '0.7rem'
          }} data-id="element-743">
              Super Admin
            </Typography>
          </Box>
          <Tooltip title="Change Password" placement="top" data-id="element-744">
            <Icon icon="lucide:key-round" width={16} height={16} color="#6B7280" data-id="element-745" />
          </Tooltip>
        </Box>
      </Box>
      <List sx={{
      mt: 1,
      px: 1.5,
      flex: 1,
      overflow: 'auto'
    }} data-id="element-746">
        {menuItems.map(item => <ListItem key={item.text} disablePadding sx={{
        mb: 0.5
      }} data-id="element-747">
            <Tooltip title={item.text} placement="right" arrow data-id="element-748">
              <ListItemButton sx={{
            borderRadius: 2,
            py: 1.2,
            backgroundColor: item.id === activePage ? '#F0F3F7' : 'transparent',
            position: 'relative',
            overflow: 'hidden',
            '&:hover': {
              backgroundColor: '#F9FAFB',
              transform: 'none'
            },
            '&::before': item.id === activePage ? {
              content: '""',
              position: 'absolute',
              left: 0,
              top: '50%',
              transform: 'translateY(-50%)',
              width: 3,
              height: '100%',
              backgroundColor: '#0052CC',
              borderRadius: '0 4px 4px 0'
            } : {},
            transition: 'none'
          }} onClick={() => handleNavigation(item.path)} data-id="element-749">
                <ListItemIcon sx={{
              minWidth: 36,
              color: item.id === activePage ? '#0052CC' : '#6B7280',
              '& svg': {
                filter: 'none',
                transition: 'none'
              }
            }} data-id="element-750">
                  <Icon icon={item.icon} width={20} height={20} data-id="element-751" />
                </ListItemIcon>
                <ListItemText primary={item.text} sx={{
              '& .MuiTypography-root': {
                fontWeight: item.id === activePage ? 600 : 500,
                color: item.id === activePage ? '#0052CC' : '#374151',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                fontSize: '0.875rem'
              }
            }} data-id="element-752" />
              </ListItemButton>
            </Tooltip>
          </ListItem>)}
      </List>
      <Box sx={{
      p: 2,
      mt: 'auto',
      borderTop: '1px solid #E5E7EB'
    }} data-id="element-753">
        <Typography variant="caption" sx={{
        color: 'text.secondary',
        display: 'block',
        textAlign: 'center',
        opacity: 0.7
      }} data-id="element-754">
          OpenRemit v2.0.0
        </Typography>
      </Box>
    </Box>;
  return <>
      {isMobile ? <Drawer anchor="left" open={open} onClose={onClose} sx={{
      '& .MuiDrawer-paper': {
        width: drawerWidth,
        boxSizing: 'border-box',
        overflow: 'hidden'
      }
    }} data-id="element-755">
          {drawerContent}
        </Drawer> : <Drawer variant="permanent" sx={{
      width: drawerWidth,
      flexShrink: 0,
      '& .MuiDrawer-paper': {
        width: drawerWidth,
        boxSizing: 'border-box',
        borderRight: '1px solid #E5E7EB',
        overflow: 'hidden'
      }
    }} data-id="element-756">
          {drawerContent}
        </Drawer>}
    </>;
};
export default Sidebar;